import {Component, OnInit, computed, inject, signal} from '@angular/core';
import {NgOptimizedImage} from '@angular/common';
import {AlertLevel} from '../../../enums/alert-level';
import {GlobalAlertService} from '../../../_services/global-alert.service';
import {
  describeAdministrationCode, describeAdministrationError
} from '../../../_shared/errors/administration-errors';
import {SelectComponent} from '../../../_ui/components/controls/select/select.component';
import {LegalAccountState} from '../../legal/_models/_enums/legal-account-state';
import {DnsEntry, DnsEntryState, DnsHttpService, DnsProvider, DnsProviderStatus} from '../_services/dns-http.service';
import {FiskalyHttpService, ManagingOrganizationSource} from '../_services/fiskaly-http.service';
import {SumupAffiliateHttpService} from '../_services/sumup-affiliate-http.service';
import {TelegramAuthBotHttpService} from '../_services/telegram-auth-bot-http.service';

/** One entry of the DNS provider picker. `cqme-select` is generic over objects, so a bare union needs a box. */
export interface DnsProviderOption {
  value: DnsProvider;
  label: string;
}

/** How a state chip is painted. The four tones the style guide's "Status badge / pill" section sanctions. */
export type ChipTone = 'ok' | 'pending' | 'blocked' | 'neutral';

/** A state chip: one sentence fragment, one tone. `null` while the answer is still being fetched. */
export interface StateChip {
  label: string;
  tone: ChipTone;
}

/**
 * **Integrationen** — every credential and key that belongs to the installation rather than to a tenant
 * (#608).
 *
 * ### What the screen is
 *
 * Four vendor cards, each built the same way: an identity header carrying **one chip that states the whole
 * condition**, then the fields that change it, then a helper line saying where the value comes from. The chip
 * is the point — an operator should be able to read the state of the installation without reading a single
 * input — so it names a state („Konfiguriert", „Fehlt"), while a field names a setting and a button names an
 * action, per the style guide's *"a form names the setting, a list reports the state"*.
 *
 * ### Fiskaly is two independent facts, not one
 *
 * The management credentials and the managing organisation id fail separately: correct credentials with no
 * organisation id still refuse **every** shard registration, permanently, with
 * `E_LEGAL_MANAGING_ORGANIZATION_NOT_CONFIGURED`. So the card carries two states rather than folding them into
 * one "fiskaly is fine" verdict, and the organisation id — the one an operator is far more likely to be
 * missing — sits in the open while the credentials live behind a disclosure, because they are already set on
 * every running installation and re-entering them is the rarer act.
 *
 * The id's own chip reports {@link ManagingOrganizationSource}: `"legacy"` is a working installation that is
 * one deleted company row away from that refusal, which is worth saying out loud rather than painting green.
 *
 * ### The Telegram token is never rendered
 *
 * `telegram_auth_bot_token` is a catalogued secret (#608), so the GET answers `configured` + the bot handle and
 * never the token. The input is therefore `type="password"`, **never prefilled**, and cleared after a
 * successful save: there is nothing to show, and an input that looked populated would invite an operator to
 * "confirm" a value the browser made up.
 *
 * ### The DNS card reports what it can SEE, not only what is stored (#637)
 *
 * The other three cards answer one question — is the credential there. DNS has a second one that no
 * credential can settle: zone and record target are **mirrored from the instance's own A record**, so a
 * perfectly valid token on an installation whose „Öffentliche Adresse" is unset (or whose host has no A
 * record) registers nothing at all. That is why the chip's blocked state is `Hauptadresse fehlt` rather than
 * anything about the token, and why a third state — `N ausstehend` — exists: a DNS failure never blocks an
 * alias save, so rows queue up in the ledger instead, and an operator who cannot see that queue has no way of
 * knowing the automation has quietly stopped working.
 *
 * ### Every refusal goes through the global alert
 *
 * There is no per-screen error banner in this app (#469). Refusals are mapped from `ApiError.code` by
 * {@link describeAdministrationError} and posted with `createAndShow`, so the operator reads German and never
 * the server's English `description`.
 */
@Component({
  selector: 'app-integrations',
  standalone: true,
  imports: [NgOptimizedImage, SelectComponent],
  templateUrl: './integrations.component.html'
})
export class IntegrationsComponent implements OnInit {
  private readonly fiskalyHttp = inject(FiskalyHttpService);
  private readonly sumupHttp = inject(SumupAffiliateHttpService);
  private readonly telegramHttp = inject(TelegramAuthBotHttpService);
  private readonly dnsHttp = inject(DnsHttpService);
  private readonly alerts = inject(GlobalAlertService);

  // ----- fiskaly -----

  /** `Unknown` is the loading state as well as the "we could not tell" state — the API models it that way. */
  protected readonly fiskalyState = signal<LegalAccountState>(LegalAccountState.Unknown);
  /** Open by default only when the credentials are not already correct — see the class doc. */
  protected readonly credentialsOpen = signal(false);
  protected readonly organizationId = signal('');
  protected readonly organizationSource = signal<ManagingOrganizationSource | null>(null);
  protected readonly savingCredentials = signal(false);
  protected readonly savingOrganization = signal(false);

  // ----- SumUp -----

  protected readonly sumupKey = signal('');
  protected readonly sumupConfigured = signal(false);
  protected readonly sumupTutorialOpen = signal(false);
  protected readonly savingSumup = signal(false);

  // ----- Telegram -----

  protected readonly telegramConfigured = signal(false);
  protected readonly telegramBotUsername = signal<string | null>(null);
  /** The typed token. Held here so a successful save can clear it — the field is never prefilled. */
  protected readonly telegramToken = signal('');
  protected readonly savingTelegram = signal(false);

  // ----- DNS (#637) -----

  /** The server's answer, or `null` while it is still being fetched. */
  protected readonly dnsStatus = signal<DnsProviderStatus | null>(null);
  /**
   * The provider the picker is showing, which is **not** the same thing as {@link dnsStatus}'s provider until
   * the save lands: picking „Cloudflare" has to reveal the token field before anything is stored, and
   * abandoning the screen must not have changed the installation.
   */
  protected readonly dnsProvider = signal<DnsProvider>('none');
  /** The typed token. Held here so a successful save can clear it — the field is never prefilled. */
  protected readonly dnsToken = signal('');
  protected readonly savingDns = signal(false);
  protected readonly reconcilingDns = signal(false);

  /**
   * The two providers this build knows, in the order an operator meets them.
   *
   * Typed mutable, not `readonly`: `cqme-select`'s `[options]` input is `TItem[]`, and `strictTemplates`
   * refuses a `readonly` array there (TS4104). The array is a `readonly` *field* instead, which is what
   * matters — it is handed back identical across change detection, so ng-select keeps an open list open.
   */
  protected readonly dnsProviderOptions: DnsProviderOption[] = [
    {value: 'none', label: 'Kein Anbieter'},
    {value: 'cloudflare', label: 'Cloudflare'}
  ];

  /** Cloudflare is the only provider with a credential, so the field exists only for it. */
  protected readonly dnsNeedsToken = computed(() => this.dnsProvider() === 'cloudflare');

  /**
   * The ledger rows worth showing: everything that is **not** settled.
   *
   * A `registered` row is the normal case and there can be hundreds of them, so listing them would bury the
   * handful that need somebody. The counts in the chip and the helper line report the whole ledger.
   */
  protected readonly dnsOpenEntries = computed<DnsEntry[]>(() =>
    (this.dnsStatus()?.entries ?? []).filter(entry => entry.state !== 'registered'));

  /**
   * The DNS chip, in strict precedence order — the states overlap and the most blocking one has to win.
   *
   * 1. no answer yet → the loading label, never a verdict;
   * 2. `none` → neutral. Nothing is wrong: aliases are still recorded in the ledger so a later switch knows
   *    what to register, there is just no provider to register them at;
   * 3. **no main record** → blocked. Zone and target are mirrored from it, so *nothing* can be registered —
   *    this outranks a pending count, because the pending rows are its consequence, not a second problem;
   * 4. `pending + failed > 0` → amber with the number. A DNS failure never blocks an alias save, so this is
   *    the only place the backlog is visible at all;
   * 5. otherwise green, naming the zone — an installation pointed at the WRONG zone looks exactly like a
   *    correct one until somebody reads the name.
   */
  protected readonly dnsChip = computed<StateChip>(() => {
    const status = this.dnsStatus();
    if (!status) return {label: 'Status wird geprüft …', tone: 'neutral'};
    if (status.provider === 'none') return {label: 'Kein Anbieter', tone: 'neutral'};
    if (!status.mainRecordFound) return {label: 'Hauptadresse fehlt', tone: 'blocked'};

    const outstanding = status.pending + status.failed;
    if (outstanding > 0) return {label: `${outstanding} ausstehend`, tone: 'pending'};

    return {label: status.zoneName ? `Konfiguriert · ${status.zoneName}` : 'Konfiguriert', tone: 'ok'};
  });

  /** The fiskaly credentials chip. */
  protected readonly fiskalyChip = computed<StateChip>(() => {
    switch (this.fiskalyState()) {
      case LegalAccountState.CredentialsCorrect:
        return {label: 'Zugangsdaten korrekt', tone: 'ok'};
      case LegalAccountState.CredentialsUnauthorized:
        return {label: 'Zugangsdaten abgelehnt', tone: 'blocked'};
      case LegalAccountState.CredentialsNotAvailable:
        return {label: 'Keine Zugangsdaten hinterlegt', tone: 'pending'};
      case LegalAccountState.CredentialsAvailable:
        return {label: 'Zugangsdaten hinterlegt', tone: 'neutral'};
      default:
        return {label: 'Status wird geprüft …', tone: 'neutral'};
    }
  });

  /**
   * The organisation-id chip.
   *
   * `"legacy"` is deliberately amber rather than green: the id is being derived from the legacy protected
   * company, nothing writes that flag any more, and the installation refuses every shard registration the day
   * that row goes. Naming the remedy in the chip is the whole reason the GET reports its source.
   */
  protected readonly organizationChip = computed<StateChip>(() => {
    switch (this.organizationSource()) {
      case 'setting':
        return {label: 'Konfiguriert', tone: 'ok'};
      case 'legacy':
        return {label: 'Legacy-Fallback, bitte eintragen', tone: 'pending'};
      case 'none':
        return {label: 'Fehlt', tone: 'blocked'};
      default:
        return {label: 'Status wird geprüft …', tone: 'neutral'};
    }
  });

  protected readonly sumupChip = computed<StateChip>(() => this.sumupConfigured()
    ? {label: 'Konfiguriert', tone: 'ok'}
    : {label: 'Kein Affiliate Key hinterlegt', tone: 'pending'});

  /**
   * The Telegram chip. It names the bot when the handle is known, because an installation with the *wrong*
   * bot's token looks exactly like one with the right bot's until somebody reads the name.
   */
  protected readonly telegramChip = computed<StateChip>(() => {
    if (!this.telegramConfigured()) return {label: 'Kein Bot hinterlegt', tone: 'pending'};
    const bot = this.telegramBotUsername();
    return {label: bot ? `Konfiguriert · @${bot}` : 'Konfiguriert', tone: 'ok'};
  });

  ngOnInit(): void {
    this.loadFiskaly();
    this.loadManagingOrganization();
    this.loadSumup();
    this.loadTelegram();
    this.loadDns();
  }

  // ----- loading -----

  protected loadFiskaly(): void {
    this.fiskalyHttp.isConfigured().subscribe({
      next: state => {
        this.fiskalyState.set(state);
        // Opened only when there is something to do: on a healthy installation the disclosure stays shut and
        // the organisation id — the field an operator is actually here for — is the first thing on the card.
        this.credentialsOpen.set(state !== LegalAccountState.CredentialsCorrect);
      },
      error: err => this.fail(err, 'Der Fiskaly-Status konnte nicht geladen werden.')
    });
  }

  protected loadManagingOrganization(): void {
    this.fiskalyHttp.getManagingOrganization().subscribe({
      next: result => {
        this.organizationId.set(result.organizationId ?? '');
        this.organizationSource.set(result.source);
      },
      error: err => this.fail(err, 'Die Fiskaly-Organisations-ID konnte nicht geladen werden.')
    });
  }

  protected loadSumup(): void {
    this.sumupHttp.get().subscribe({
      next: result => {
        this.sumupKey.set(result.key ?? '');
        this.sumupConfigured.set(!!result.key);
      },
      error: err => this.fail(err, 'Der SumUp Affiliate Key konnte nicht geladen werden.')
    });
  }

  protected loadTelegram(): void {
    this.telegramHttp.get().subscribe({
      next: result => {
        this.telegramConfigured.set(result.configured);
        this.telegramBotUsername.set(result.botUsername);
      },
      error: err => this.fail(err, 'Der Telegram-Bot konnte nicht geladen werden.')
    });
  }

  protected loadDns(): void {
    this.dnsHttp.get().subscribe({
      next: status => this.adoptDns(status),
      error: err => this.fail(err, 'Die DNS-Einstellungen konnten nicht geladen werden.')
    });
  }

  // ----- saving -----

  protected saveCredentials(email: string, password: string): void {
    if (this.savingCredentials()) return;
    this.savingCredentials.set(true);
    // Back to Unknown for the round trip: the chip must not keep claiming the OLD verdict while a new pair is
    // being checked, which is the one moment its answer is certainly out of date.
    this.fiskalyState.set(LegalAccountState.Unknown);

    this.fiskalyHttp.setCredentials(email, password).subscribe({
      next: state => {
        this.savingCredentials.set(false);
        this.fiskalyState.set(state);
        // The server verifies before it writes anything (#896): a REJECTED pair is a 200 carrying
        // `CredentialsUnauthorized`, not an rxjs error, and nothing was stored — the credentials already on
        // file, if any, are still the ones in force. Only an ACCEPTED pair is a save; a rejection gets its own
        // sentence rather than the „gespeichert" one, which would tell the operator the opposite of what
        // happened.
        if (state === LegalAccountState.CredentialsCorrect) {
          this.succeed('Die Fiskaly-Zugangsdaten wurden gespeichert.');
        } else {
          this.alerts.createAndShow('Fehler',
            'Der Provider hat die eingegebenen Zugangsdaten abgelehnt. Sie wurden nicht gespeichert. Bitte '
            + 'prüfen Sie E-Mail-Adresse und Passwort.', AlertLevel.error);
        }
      },
      error: err => {
        this.savingCredentials.set(false);
        // Re-read to recover the chip: a network/5xx failure here leaves the state genuinely unknown, never
        // because the server might have stored a pair it could not verify — #896 made that impossible, a
        // rejection is never written at all.
        this.loadFiskaly();
        this.fail(err, 'Die Fiskaly-Zugangsdaten konnten nicht gespeichert werden.');
      }
    });
  }

  protected saveOrganization(organizationId: string): void {
    if (this.savingOrganization()) return;
    this.savingOrganization.set(true);

    this.fiskalyHttp.setManagingOrganization(organizationId.trim()).subscribe({
      next: result => {
        this.savingOrganization.set(false);
        this.organizationId.set(result.organizationId ?? '');
        this.organizationSource.set(result.source);
        this.succeed('Die Fiskaly-Organisations-ID wurde gespeichert.');
      },
      error: err => {
        this.savingOrganization.set(false);
        this.fail(err, 'Die Fiskaly-Organisations-ID konnte nicht gespeichert werden.');
      }
    });
  }

  protected saveSumup(key: string): void {
    if (this.savingSumup()) return;
    this.savingSumup.set(true);

    this.sumupHttp.set(key.trim()).subscribe({
      next: result => {
        this.savingSumup.set(false);
        this.sumupKey.set(result.key ?? '');
        this.sumupConfigured.set(!!result.key);
        this.succeed('Der SumUp Affiliate Key wurde gespeichert.');
      },
      error: err => {
        this.savingSumup.set(false);
        this.fail(err, 'Der SumUp Affiliate Key konnte nicht gespeichert werden.');
      }
    });
  }

  protected saveTelegramToken(token: string): void {
    if (this.savingTelegram()) return;
    this.savingTelegram.set(true);

    this.telegramHttp.set(token.trim()).subscribe({
      next: result => {
        this.savingTelegram.set(false);
        this.telegramConfigured.set(result.configured);
        this.telegramBotUsername.set(result.botUsername);
        // Cleared, never echoed: the stored token is a secret the server will not hand back, so leaving the
        // typed one in the field is the only way it could ever be read off this screen.
        this.telegramToken.set('');
        this.succeed('Das Telegram-Bot-Token wurde gespeichert.');
      },
      error: err => {
        this.savingTelegram.set(false);
        this.fail(err, 'Das Telegram-Bot-Token konnte nicht gespeichert werden.');
      }
    });
  }

  /**
   * Stores the picked provider and, when one was typed, its token.
   *
   * The server verifies the credentials before it changes anything, so a refusal leaves the installation as it
   * was — which is why nothing here is optimistically applied and the card re-renders from the answer.
   */
  protected saveDns(): void {
    if (this.savingDns()) return;
    this.savingDns.set(true);

    this.dnsHttp.set(this.dnsProvider(), this.dnsToken().trim()).subscribe({
      next: status => {
        this.savingDns.set(false);
        this.adoptDns(status);
        this.succeed('Die DNS-Einstellungen wurden gespeichert.');
      },
      error: err => {
        this.savingDns.set(false);
        // The picker deliberately keeps the refused choice: it is what the operator has to correct, and
        // snapping it back to the stored provider reads as "the click did not register".
        this.fail(err, 'Die DNS-Einstellungen konnten nicht gespeichert werden.');
      }
    });
  }

  /** Drives every pending/failed ledger row once, instead of waiting for the recurring job. */
  protected reconcileDns(): void {
    if (this.reconcilingDns()) return;
    this.reconcilingDns.set(true);

    this.dnsHttp.reconcile().subscribe({
      next: status => {
        this.reconcilingDns.set(false);
        this.adoptDns(status);
        this.succeed('Der Abgleich wurde ausgeführt.');
      },
      error: err => {
        this.reconcilingDns.set(false);
        this.fail(err, 'Der Abgleich konnte nicht ausgeführt werden.');
      }
    });
  }

  /**
   * Adopts a server answer: status, picker, and a cleared token field.
   *
   * The token is cleared on every answer, not only after a save — the server never hands one back, so
   * anything left in the field is the operator's own keystrokes sitting in the DOM long after they have been
   * stored. The picker follows the ANSWER rather than the click, so a switch the server did not make (a
   * rejected credential leaves `provider` where it was) is visible as such on the next load.
   */
  private adoptDns(status: DnsProviderStatus): void {
    this.dnsStatus.set(status);
    this.dnsProvider.set(status.provider);
    this.dnsToken.set('');
  }

  // ----- disclosures -----

  protected toggleCredentials(): void {
    this.credentialsOpen.update(open => !open);
  }

  protected toggleSumupTutorial(): void {
    this.sumupTutorialOpen.update(open => !open);
  }

  // ----- display -----

  /**
   * The value of the input that raised `event`.
   *
   * The three prefilled fields are **controlled** — `[value]` bound to a signal, `(input)` writing it back —
   * rather than read off a template reference at save time, and that is load-bearing rather than style. A
   * property binding is written only when the *evaluated* value changes, so with the component holding a value
   * the DOM does not, the server's normalisation (a trimmed id, an address whose trailing slash was stripped)
   * evaluates to the value the signal already had and Angular writes nothing — the field keeps showing what
   * was typed while the installation runs on something else. Owning the rendered state is the documented fix
   * for that class of bug; see the style guide's note on the toggle that does not spring back.
   */
  protected value(event: Event): string {
    return (event.target as HTMLInputElement).value;
  }

  /**
   * `cqme-select` declares `currentChange` as `TValue | TValue[] | undefined` even single-valued, because the
   * same component also does `[multiple]`. Normalised once here rather than cast in the template.
   *
   * `undefined` (the control cleared) is ignored: the picker is not clearable, and a provider of "nothing" is
   * not a state this screen can save.
   */
  protected onDnsProviderPicked(value: DnsProvider | DnsProvider[] | undefined): void {
    const provider = Array.isArray(value) ? value[0] : value;
    if (!provider) return;

    this.dnsProvider.set(provider);
    // Switching away takes the field off the screen; without this the typed credential would still be in
    // memory, and would still be sent with the next save of a provider it does not belong to.
    if (provider === 'none') this.dnsToken.set('');
  }

  /**
   * The German word for a ledger state.
   *
   * One entry per member, so a regressed mapping fails by name rather than falling through to the wire value —
   * `pendingAdd` on a German screen is the kind of thing that ships.
   */
  private static readonly DNS_STATES: Record<DnsEntryState, string> = {
    pendingAdd: 'Wird angelegt',
    registered: 'Aktiv',
    pendingRemove: 'Wird entfernt',
    failed: 'Fehlgeschlagen'
  };

  protected dnsStateLabel(state: DnsEntryState): string {
    return IntegrationsComponent.DNS_STATES[state];
  }

  /** A failed row is the only one that is actually wrong; the two pending ones are simply not done yet. */
  protected dnsStateTone(state: DnsEntryState): ChipTone {
    return state === 'failed' ? 'blocked' : 'pending';
  }

  /**
   * Why a ledger row failed, in German where this build knows the code.
   *
   * The raw `lastError` is the *provider's* English sentence, which the style guide keeps off the screen — but
   * a row with an unmapped code and no sentence would report nothing at all, and an English diagnostic beats
   * "it failed". So the code wins, the provider's text is the fallback, and `''` means "no reason given".
   */
  protected dnsEntryReason(entry: DnsEntry): string {
    const known = entry.lastErrorCode ? describeAdministrationCode(entry.lastErrorCode, '') : '';
    return known || entry.lastError || '';
  }

  /**
   * The sentence for whatever is blocking the card, or `''` when nothing is.
   *
   * The status probe reports its own failure on a 200 (`status.error`), because refusing the read would leave
   * an operator staring at an empty card while the installation registers nothing. Rendered from the code, so
   * a rejected token and a missing zone read as the two different problems they are — the missing main record
   * keeps its own sentence below, which names the field on the sibling screen that fixes it.
   */
  protected dnsBlockingReason(status: DnsProviderStatus): string {
    if (status.provider === 'none' || !status.error) return '';
    if (status.error.code === 'E_DNS_MAIN_RECORD_NOT_FOUND') return '';
    return describeAdministrationCode(status.error.code, 'Der DNS-Anbieter konnte nicht abgefragt werden.');
  }

  /**
   * The surface/text pair for a chip's tone.
   *
   * Written as whole literal class strings so Tailwind's content scanner (`./src/**\/*.{html,ts}`) emits them —
   * a class assembled from fragments at runtime would compile to nothing. The pairs are the measured ones from
   * the style guide's contrast table: `green-700`/`amber-700`/`red-700` on their `-50` surfaces (5.01:1 /
   * 4.85:1 / 5.91:1) and `gray-600` on `gray-100` (6.86:1). The obvious `-600` shades all fail AA at this
   * chip's `text-xs`, and this repo has shipped that defect twice.
   */
  private static readonly CHIP_TONES: Record<ChipTone, string> = {
    ok: 'bg-green-50 text-green-700',
    pending: 'bg-amber-50 text-amber-700',
    blocked: 'bg-red-50 text-red-700',
    neutral: 'bg-gray-100 text-gray-600'
  };

  protected chipClass(chip: StateChip): string {
    return this.toneClass(chip.tone);
  }

  /** The same pairs for a bare tone — the DNS ledger rows carry a state, not a whole {@link StateChip}. */
  protected toneClass(tone: ChipTone): string {
    return IntegrationsComponent.CHIP_TONES[tone];
  }

  // ----- alerts -----

  /** Posts a refusal as German, mapped from its `code`; the server's English `description` never renders. */
  private fail(err: unknown, fallback: string): void {
    this.alerts.createAndShow('Fehler', describeAdministrationError(err, fallback), AlertLevel.error);
  }

  private succeed(message: string): void {
    this.alerts.createAndShow('Gespeichert', message, AlertLevel.success);
  }
}
